import { useState, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import { BASE_URL } from "../../../config/axios";

const ProjectFilter = ({ onApply, hideSector = false }) => {
  const [filters, setFilters] = useState({
    project: "",
    sector: "",
    workType: "",
    companyName: "",
    loaStartDate: "",
    loaEndDate: "",
    completionStartDate: "",
    completionEndDate: "",
  });

  // ✅ Controlled select states
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedWorkType, setSelectedWorkType] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedDateType, setSelectedDateType] = useState(null);

  const [projectList, setProjectList] = useState([]);
  const [sectorList, setSectorList] = useState([]);
  const [workTypeList, setWorkTypeList] = useState([]);
  const [companyList, setCompanyList] = useState([]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  // ✅ Clear date fields when date type is cleared
  useEffect(() => {
    if (!selectedDateType) {
      setFilters((prev) => ({
        ...prev,
        loaStartDate: "",
        loaEndDate: "",
        completionStartDate: "",
        completionEndDate: "",
      }));
    }
  }, [selectedDateType]);

  const fetchDropdownData = async () => {
    try {
      const [projectRes, sectorRes, workTypeRes, companyRes] = await Promise.all([
        axios.get(`${BASE_URL}/dpr/projectdetails/`),
        axios.get(`${BASE_URL}/dpr/subconsultant/`),
        axios.get(`${BASE_URL}/dpr/worktype/`),
        axios.get(`${BASE_URL}/dpr/subcompany/`),
      ]);
      setProjectList(projectRes.data);
      setSectorList(sectorRes.data);
      setWorkTypeList(workTypeRes.data);
      setCompanyList(companyRes.data);
    } catch (err) {
      console.error("Dropdown fetch error:", err);
    }
  };

  const projectOptions = projectList.map((i) => ({
    value: i.id,
    label: i.project_short_name || i.project_name,
  }));
  const sectorOptions = sectorList.map((i) => ({ value: i.id, label: i.name }));
  const workTypeOptions = workTypeList.map((i) => ({ value: i.id, label: i.name }));
  const companyOptions = companyList.map((i) => ({ value: i.id, label: i.company_name }));

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const safe = (val) => (val !== "" && val !== null && val !== undefined ? val : "null");

  const handleApply = () => {
    const formattedFilters = {
      project: safe(filters.project),
      sector: safe(filters.sector),
      workType: safe(filters.workType),
      companyName: safe(filters.companyName),
      loaStartDate: safe(filters.loaStartDate),
      loaEndDate: safe(filters.loaEndDate),
      completionStartDate: safe(filters.completionStartDate),
      completionEndDate: safe(filters.completionEndDate),
    };
    console.log("🔥 PROJECT FILTER APPLY:", formattedFilters);
    onApply(formattedFilters);
  };

  // ✅ Clear resets all selects visually + filter state
  const handleClear = () => {
    setFilters({
      project: "",
      sector: "",
      workType: "",
      companyName: "",
      loaStartDate: "",
      loaEndDate: "",
      completionStartDate: "",
      completionEndDate: "",
    });
    setSelectedProject(null);
    setSelectedSector(null);
    setSelectedWorkType(null);
    setSelectedCompany(null);
    setSelectedDateType(null);
    onApply({
      project: "null",
      sector: "null",
      workType: "null",
      companyName: "null",
      loaStartDate: "null",
      loaEndDate: "null",
      completionStartDate: "null",
      completionEndDate: "null",
    });
  };

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    container: (base) => ({
      ...base,
      minWidth: 110,
      maxWidth: 260,
      width: "auto",
      flex: "1 1 auto",
    }),
    control: (base) => ({
      ...base,
      minHeight: 32,
      height: "auto",
      border: "none",
      borderRadius: 0,
      boxShadow: "none",
      background: "transparent",
      cursor: "pointer",
      flexWrap: "wrap",
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
    singleValue: (base) => ({
      ...base,
      fontSize: 12,
      color: "#111827",
      whiteSpace: "normal",
      wordBreak: "break-word",
      maxWidth: "100%",
    }),
    placeholder: (base) => ({ ...base, fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }),
    input: (base) => ({ ...base, fontSize: 12, margin: 0, padding: 0 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px", color: "#9ca3af" }),
    clearIndicator: (base) => ({ ...base, padding: "0 4px", color: "#9ca3af" }),
    menu: (base) => ({ ...base, fontSize: 12, minWidth: 160, width: "max-content", maxWidth: 320 }),
    option: (base, state) => ({
      ...base,
      fontSize: 12,
      padding: "7px 12px",
      backgroundColor: state.isSelected ? "#111827" : state.isFocused ? "#f3f4f6" : "#fff",
      color: state.isSelected ? "#fff" : "#111827",
      whiteSpace: "normal",
      wordBreak: "break-word",
      cursor: "pointer",
    }),
  };

  const iconStyle = { flexShrink: 0, marginTop: 1 };

  return (
    <>
      <style>{`
        .pf-bar { display:flex;flex-wrap:wrap;align-items:stretch;gap:0;border:0.5px solid #d1d5db;border-radius:8px;overflow:visible;background:#fff;margin-bottom:14px; }
        .pf-chip { display:flex;align-items:center;gap:6px;padding:0 12px;min-height:38px;height:auto;border-right:0.5px solid #e5e7eb;flex:1 1 auto;min-width:110px;max-width:260px;position:relative;flex-wrap:wrap; }
        .pf-chip:last-of-type { border-right:none; }
        .pf-chip-range { display:flex;align-items:center;gap:6px;padding:0 12px;min-height:38px;height:auto;border-right:0.5px solid #e5e7eb;flex:1 1 200px;flex-wrap:wrap; }
        .pf-chip-range.disabled { opacity:0.45;pointer-events:none; }
        .pf-range-label { font-size:11px;color:#9ca3af;white-space:nowrap;flex-shrink:0; }
        .pf-date-input { font-size:11px;border:none;outline:none;background:transparent;color:#111827;min-width:100px;max-width:130px;flex:1 1 auto;cursor:pointer;padding:4px 0;font-family:inherit; }
        .pf-date-input:disabled { color:#9ca3af;cursor:not-allowed; }
        .pf-sep { font-size:11px;color:#d1d5db;flex-shrink:0; }
        .pf-btn-apply { height:28px;padding:0 14px;background:#111827;color:#fff;border:none;border-radius:5px;font-size:12px;font-weight:500;cursor:pointer;white-space:nowrap;line-height:1; }
        .pf-btn-apply:hover { background:#2576bc; }
        .pf-btn-clear { height:28px;padding:0 10px;background:transparent;color:#6b7280;border:0.5px solid #e5e7eb;border-radius:5px;font-size:12px;cursor:pointer;white-space:nowrap;line-height:1; }
        .pf-btn-clear:hover { background:#f9fafb; }
        @media (max-width:900px) {
          .pf-bar { flex-wrap:wrap; }
          .pf-chip,.pf-chip-range { border-right:none;border-bottom:0.5px solid #e5e7eb;flex:1 1 160px;max-width:100%; }
          .pf-btn-row { width:100%;border-top:0.5px solid #e5e7eb;justify-content:flex-end; }
        }
      `}</style>

      <div className="pf-bar">

        {/* PROJECT */}
        <div className="pf-chip">
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3a76bd" strokeWidth="2">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          <Select
            styles={selectStyles}
            options={projectOptions}
            placeholder="Project"
            isClearable
            value={selectedProject}
            onChange={(opt) => {
              setSelectedProject(opt);
              setFilters((prev) => ({ ...prev, project: opt ? opt.value : "" }));
            }}
            menuPortalTarget={document.body}
          />
        </div>

        {/* SECTOR */}
        {!hideSector && (
          <div className="pf-chip">
            <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
            <Select
              styles={selectStyles}
              options={sectorOptions}
              placeholder="Sector"
              isClearable
              value={selectedSector}
              onChange={(opt) => {
                setSelectedSector(opt);
                setFilters((prev) => ({ ...prev, sector: opt ? opt.value : "" }));
              }}
              menuPortalTarget={document.body}
            />
          </div>
        )}

        {/* WORK TYPE */}
        <div className="pf-chip">
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
          </svg>
          <Select
            styles={selectStyles}
            options={workTypeOptions}
            placeholder="Work Type"
            isClearable
            value={selectedWorkType}
            onChange={(opt) => {
              setSelectedWorkType(opt);
              setFilters((prev) => ({ ...prev, workType: opt ? opt.value : "" }));
            }}
            menuPortalTarget={document.body}
          />
        </div>

        {/* COMPANY */}
        <div className="pf-chip">
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <Select
            styles={selectStyles}
            options={companyOptions}
            placeholder="Company"
            isClearable
            value={selectedCompany}
            onChange={(opt) => {
              setSelectedCompany(opt);
              setFilters((prev) => ({ ...prev, companyName: opt ? opt.value : "" }));
            }}
            menuPortalTarget={document.body}
          />
        </div>

        {/* DATE TYPE */}
        <div className="pf-chip">
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={selectedDateType ? "#d97706" : "#9ca3af"} strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <Select
            styles={selectStyles}
            placeholder="Date Type"
            isClearable
            value={selectedDateType}
            options={[
              { value: "loa", label: "LOA Date" },
              { value: "completion", label: "Completion Date" },
            ]}
            onChange={(opt) => setSelectedDateType(opt || null)}
            menuPortalTarget={document.body}
          />
        </div>

        {/* DATE RANGE */}
        <div className={`pf-chip-range ${!selectedDateType ? "disabled" : ""}`}>
          <svg style={iconStyle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={selectedDateType ? "#d97706" : "#9ca3af"} strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span className="pf-range-label">
            {selectedDateType?.value === "loa" ? "LOA" : selectedDateType?.value === "completion" ? "Completion" : "Range"}
          </span>
          <input
            className="pf-date-input"
            type="date"
            disabled={!selectedDateType}
            value={selectedDateType?.value === "loa" ? filters.loaStartDate : filters.completionStartDate}
            onChange={(e) =>
              handleChange({
                target: {
                  name: selectedDateType?.value === "loa" ? "loaStartDate" : "completionStartDate",
                  value: e.target.value,
                },
              })
            }
          />
          <span className="pf-sep">—</span>
          <input
            className="pf-date-input"
            type="date"
            disabled={!selectedDateType}
            value={selectedDateType?.value === "loa" ? filters.loaEndDate : filters.completionEndDate}
            onChange={(e) =>
              handleChange({
                target: {
                  name: selectedDateType?.value === "loa" ? "loaEndDate" : "completionEndDate",
                  value: e.target.value,
                },
              })
            }
          />
        </div>

        {/* BUTTONS */}
        <div style={{ display:"flex", alignItems:"center", gap:"6px", padding:"0 10px", borderLeft:"0.5px solid #e5e7eb", flexShrink:0, minHeight:"38px" }}>
          <button className="pf-btn-apply" onClick={handleApply}>Apply</button>
          <button className="pf-btn-clear" onClick={handleClear}>Clear</button>
        </div>

      </div>
    </>
  );
};

export default ProjectFilter;